import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ShopifyProduct, storefrontApiRequest, SHOPIFY_STORE_PERMANENT_DOMAIN } from '@/lib/shopify';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

const MAX_QUANTITY_PER_ITEM = 99;
const MAX_TOTAL_ITEMS = 999;

export interface CartItem {
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  quantity: number;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
}

interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  
  addItem: (item: CartItem) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  removeItem: (variantId: string) => void;
  clearCart: () => void;
  setCartId: (cartId: string) => void;
  setCheckoutUrl: (url: string) => void;
  setLoading: (loading: boolean) => void;
  createCheckout: () => Promise<void>;
}

const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                  }
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

async function createStorefrontCheckout(items: CartItem[]): Promise<string> {
  try {
    const lines = items.map(item => ({
      quantity: item.quantity,
      merchandiseId: item.variantId,
    }));

    const cartData = await storefrontApiRequest(CART_CREATE_MUTATION, {
      input: {
        lines,
      },
    });

    logger.info('Cart Create Response', cartData);

    if (cartData.data.cartCreate.userErrors.length > 0) {
      throw new Error(`Cart creation failed: ${cartData.data.cartCreate.userErrors.map((e: any) => e.message).join(', ')}`);
    }

    const cart = cartData.data.cartCreate.cart;
    
    if (!cart.checkoutUrl) {
      throw new Error('No checkout URL returned from Shopify');
    }

    // CRITICAL: Replace custom domain with Shopify permanent domain
    // This prevents the checkout URL from being treated as an internal React route
    const url = new URL(cart.checkoutUrl);
    
    // Force the Shopify permanent domain to ensure external checkout
    url.hostname = SHOPIFY_STORE_PERMANENT_DOMAIN;
    url.protocol = 'https:';
    url.searchParams.set('channel', 'online_store');
    
    const checkoutUrl = url.toString();
    
    // Log to both logger and console for easy debugging
    logger.info('Checkout URL created', checkoutUrl);
    console.log('🛒 Shopify Checkout URL:', checkoutUrl);
    
    return checkoutUrl;
  } catch (error) {
    logger.error('Error creating storefront checkout:', error);
    throw error;
  }
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,

      addItem: (item) => {
        const { items } = get();
        const existingItem = items.find(i => i.variantId === item.variantId);
        
        if (existingItem) {
          const newQuantity = Math.min(
            existingItem.quantity + item.quantity,
            MAX_QUANTITY_PER_ITEM
          );
          
          if (newQuantity === existingItem.quantity) {
            toast.warning('Maximum quantity reached', {
              description: `You can only add up to ${MAX_QUANTITY_PER_ITEM} of this item`,
              position: 'top-center',
            });
            return;
          }
          
          set({
            checkoutUrl: null,
            items: items.map(i =>
              i.variantId === item.variantId
                ? { ...i, quantity: newQuantity }
                : i
            )
          });
        } else {
          const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
          if (totalItems >= MAX_TOTAL_ITEMS) {
            toast.warning('Cart limit reached', {
              description: `You can only have up to ${MAX_TOTAL_ITEMS} items in your cart`,
              position: 'top-center',
            });
            return;
          }
          
          set({ 
            checkoutUrl: null,
            items: [...items, { 
              ...item, 
              quantity: Math.min(item.quantity, MAX_QUANTITY_PER_ITEM) 
            }] 
          });
        }
        
        toast.success('Added to cart', {
          description: `${item.product.node.title} has been added to your cart`,
          position: 'top-center',
        });
      },

      updateQuantity: (variantId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(variantId);
          return;
        }
        
        const cappedQuantity = Math.min(quantity, MAX_QUANTITY_PER_ITEM);
        
        if (cappedQuantity < quantity) {
          toast.warning('Maximum quantity reached', {
            description: `You can only have up to ${MAX_QUANTITY_PER_ITEM} of this item`,
            position: 'top-center',
          });
        }
        
        set({
          checkoutUrl: null,
          items: get().items.map(item =>
            item.variantId === variantId ? { ...item, quantity: cappedQuantity } : item
          )
        });
      },

      removeItem: (variantId) => {
        set({
          checkoutUrl: null,
          items: get().items.filter(item => item.variantId !== variantId)
        });
      },

      clearCart: () => {
        set({ items: [], cartId: null, checkoutUrl: null });
      },

      setCartId: (cartId) => set({ cartId }),
      setCheckoutUrl: (checkoutUrl) => set({ checkoutUrl }),
      setLoading: (isLoading) => set({ isLoading }),

      createCheckout: async () => {
        const { items, setLoading, setCheckoutUrl } = get();
        if (items.length === 0) return;

        // Always clear any stale checkout URL before attempting to create a new one
        setCheckoutUrl(null as unknown as string);
        setLoading(true);
        try {
          const checkoutUrl = await createStorefrontCheckout(items);
          setCheckoutUrl(checkoutUrl);
        } catch (error) {
          logger.error('Failed to create checkout:', error);
          toast.error('Failed to create checkout', {
            description: 'Please try again',
          });
          throw error;
        } finally {
          setLoading(false);
        }
      }

    }),
    {
      name: 'shopify-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items, cartId: state.cartId }),
    }

  )
);
